import { C4_COLS, C4_ROWS, type Connect4State, type PlayerIndex } from "@playpal/core";

interface Props {
	state: Connect4State;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

const DISC = ["bg-rose-500", "bg-sky-500"] as const;

/**
 * What is in a column, read from the bottom up — the way the discs stack, and
 * the way a child looking at it would describe it.
 *
 * The forty-two cells are `span`s carrying a colour class and nothing else: no
 * text, no role, no label. Sighted players read the board; everybody else had
 * no way to know what was in it, because "Drop into column 3" is the same
 * sentence whether that column is empty or nearly full. The colours are not the
 * information either — which disc is yours changes with your seat, so this says
 * "yours" and "theirs" rather than "red" and "blue".
 */
function describeColumn(state: Connect4State, column: number, seat: PlayerIndex): string {
	const stack: string[] = [];
	// Bottom row first.
	for (let row = C4_ROWS - 1; row >= 0; row--) {
		const cell = state.board[row * C4_COLS + column];
		if (cell === null) break;
		stack.push(cell === seat ? "yours" : "theirs");
	}

	// Always starts "Column N," so the name has a stable, greppable shape.
	const where = `Column ${column + 1}`;
	if (stack.length === 0) return `${where}, empty`;
	if (stack.length === C4_ROWS) return `${where}, full: ${stack.join(", ")} from the bottom`;
	return `${where}, ${stack.length} of ${C4_ROWS}: ${stack.join(", ")} from the bottom`;
}

export function Connect4Board({ state, seat, onMove }: Props) {
	const myTurn = state.turn === seat && !state.result;
	const columns = Array.from({ length: C4_COLS }, (_, column) => column);
	const columnFull = (column: number) => state.board[column] !== null;

	return (
		<div className="board-frame board-lg">
			<div className="rounded-3xl bg-indigo-600/90 p-2 shadow-xl sm:p-3">
				<div className="grid grid-cols-7 gap-1.5 sm:gap-2">
					{columns.map((column) => (
						<button
							key={column}
							type="button"
							disabled={!myTurn || columnFull(column)}
							// What is in it first, then what you can do about it — so the
							// name is the same shape whether or not it is your go.
							aria-label={`${describeColumn(state, column, seat)}${
								myTurn && !columnFull(column) ? ". Drop here" : ""
							}`}
							onClick={() => onMove({ column })}
							className="group flex flex-col gap-1.5 rounded-2xl p-1 transition enabled:hover:bg-white/15 disabled:cursor-not-allowed sm:gap-2"
						>
							{Array.from({ length: C4_ROWS }, (_, row) => {
								const index = row * C4_COLS + column;
								const cell = state.board[index];
								const winning = state.winningLine?.includes(index) ?? false;
								return (
									<span
										key={index}
										// The button above says what is in the column; announcing
										// forty-two more nodes on top of that is noise, not access.
										aria-hidden="true"
										className={`block aspect-square w-full rounded-full ring-inset transition ${
											cell === null
												? "bg-indigo-950/40 group-enabled:group-hover:bg-indigo-950/25"
												: `${DISC[cell]} ${index === state.lastDrop ? "animate-drop" : ""}`
										} ${winning ? "ring-4 ring-amber-300" : ""}`}
									/>
								);
							})}
						</button>
					))}
				</div>
			</div>
			<p className="mt-3 text-center text-sm text-indigo-900/70 dark:text-indigo-100/70">
				Tap a column to drop your disc.
			</p>
		</div>
	);
}
